import { useState, FormEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

type Step = 'form' | 'sent';

export default function ForgotPassword() {
  const [email, setEmail]         = useState('');
  const [error, setError]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep]           = useState<Step>('form');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const validate = () => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address.';
    }
    return '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true);
    /* ── Password reset will be wired to Supabase once connected ── */
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setStep('sent');
  };

  return (
    <div className="min-h-screen bg-navy flex flex-col">

      {/* Subtle top gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-gold/5 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold/3 blur-3xl" />
      </div>

      {/* Back to site */}
      <div className="relative z-10 px-6 py-6 flex justify-between items-center">
        <Link to="/" className="flex-shrink-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
            alt="Foreclosure Recovery"
            className="h-10 w-auto object-contain"
          />
        </Link>
        <Link
          to="/login"
          className="flex items-center gap-2 text-white/40 hover:text-white/80 transition-colors text-sm"
        >
          <i className="ri-arrow-left-line text-sm" />
          Back to Sign In
        </Link>
      </div>

      {/* Center card */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[440px]">

          {step === 'form' ? (
            <>
              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gold/10 border border-gold/20 mb-8">
                <i className="ri-lock-password-line text-gold text-3xl" />
              </div>

              {/* Heading */}
              <h1 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-2">
                Reset Your Password
              </h1>
              <p className="text-white/45 text-sm leading-relaxed mb-10 max-w-sm">
                Enter the email address linked to your account. We&apos;ll send you a secure link to reset your password.
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
                <div>
                  <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      autoComplete="email"
                      autoFocus
                      className={`w-full pl-10 pr-4 py-3.5 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                        error ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                      }`}
                    />
                  </div>
                  {error && (
                    <p className="flex items-center gap-1.5 text-red-400 text-xs mt-2">
                      <i className="ri-error-warning-line text-sm" />
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-gold text-navy-dark font-bold text-base rounded-xl hover:bg-gold-light transition-all duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      Sending Reset Link…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg" />
                      Send Reset Link
                    </>
                  )}
                </button>
              </form>

              {/* Footer links */}
              <div className="mt-8 pt-6 border-t border-white/10 flex flex-col gap-3 text-center">
                <p className="text-white/40 text-sm">
                  Remember your password?{' '}
                  <Link to="/login" className="text-gold font-semibold hover:text-gold-light transition-colors">
                    Sign In
                  </Link>
                </p>
                <p className="text-white/30 text-sm">
                  Don&apos;t have an account?{' '}
                  <Link to="/signup" className="text-white/50 hover:text-gold transition-colors font-medium">
                    Create one free
                  </Link>
                </p>
              </div>
            </>
          ) : (
            /* ── Sent confirmation ── */
            <div className="text-center animate-fade-up">
              {/* Animated envelope */}
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="w-24 h-24 flex items-center justify-center rounded-full bg-gold/15 border-2 border-gold/30">
                  <i className="ri-mail-check-line text-gold text-5xl" />
                </div>
                <div className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center rounded-full bg-green-500 border-2 border-navy">
                  <i className="ri-check-line text-white text-xs font-bold" />
                </div>
              </div>

              <h2 className="font-playfair text-3xl font-bold text-white mb-3">
                Check Your Inbox
              </h2>
              <p className="text-white/50 text-sm leading-relaxed mb-2 max-w-sm mx-auto">
                We sent a password reset link to:
              </p>
              <p className="text-gold font-semibold mb-8 text-base break-all">{email}</p>

              {/* Instructions */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-left mb-8">
                <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-4">What to do next</p>
                <ol className="flex flex-col gap-3">
                  {[
                    'Open the email from Foreclosure Recovery',
                    'Click the "Reset Password" button in the email',
                    'Choose a new secure password',
                    'Sign in with your new password',
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <span className="text-white/60 text-sm">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Resend & back */}
              <div className="flex flex-col gap-3">
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 w-full py-3.5 bg-gold text-navy-dark font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap"
                >
                  <i className="ri-login-box-line" />
                  Back to Sign In
                </Link>
                <button
                  type="button"
                  onClick={() => { setStep('form'); setEmail(''); }}
                  className="text-white/30 text-sm hover:text-white/60 transition-colors cursor-pointer"
                >
                  Use a different email address
                </button>
              </div>

              <p className="text-white/20 text-xs mt-6">
                Didn&apos;t receive it? Check your spam folder or{' '}
                <Link to="/contact" className="text-white/40 hover:text-gold transition-colors underline">
                  contact support
                </Link>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
